import { ENDPOINT_FEEDBACK } from "@constants/urls";
import { currentAppVersion } from "@helpers/expo-extras";
import { RUN_LANGSMITH_URL } from "@memorang/features/src/feedback/constants";
import { Platform } from "react-native";
import { useReviewStore } from "../hooks/useReviewStore";

const NUM_SESSION_THRESHOLD = 3;
const NUM_DAYS_THRESHOLD = 3;

const getShouldShowReviewPrompt = (isPremiumUser: boolean) => {
	const numSessionCompleted =
		useReviewStore.getState().numSessionCompleted ?? 0;
	const lastShownPromptDate = useReviewStore.getState().lastShownPromptDate;
	const lastAppVersion = useReviewStore.getState().lastAppVersion;

	if (
		!isPremiumUser ||
		numSessionCompleted < NUM_SESSION_THRESHOLD ||
		lastAppVersion === currentAppVersion
	) {
		return false;
	}

	// Check if the user has not been asked for feedback within the last 3 days
	const currentDate = Date.now();
	const daysSinceLastPrompt = lastShownPromptDate
		? (currentDate - new Date(lastShownPromptDate).getTime()) /
			(1000 * 60 * 60 * 24)
		: Number.POSITIVE_INFINITY; // If never shown, treat as infinity to always allow
	if (daysSinceLastPrompt < NUM_DAYS_THRESHOLD) {
		return false;
	}

	return true;
};

export type FeedbackType = "Review" | "Deletion" | "Content Flagged";

type FeedbackParams = {
	email: string;
	feedback: string;
	type: FeedbackType;
	rating?: "positive" | "negative";
	appLanguage: string;
	examName?: string;
	view: string;
	projectId: number;
	organizationId: number;
	productId: number;
	currentScreen?: string;
	featureName?: string;
	itemId?: string;
	sessionId?: string;
	responseId?: string;
	threadId?: string;
	runId?: string;
	itemVersionId?: string;
	sessionType?: string;
	context?: string;
};

/**
 * Assembles the optional feedback fields shared by both send paths (this
 * feature's `sendFeedback` and `useFeedbackParams` -> the shared package
 * `sendFeedback`), so new fields are added in exactly one place.
 */
export const buildOptionalFeedbackParams = ({
	sessionId,
	itemId,
	threadId,
	runId,
	rating,
	featureName,
	responseId,
	context,
	sessionType,
}: {
	sessionId?: string | number;
	itemId?: string;
	threadId?: string;
	runId?: string;
	rating?: "positive" | "negative";
	featureName?: string;
	responseId?: string;
	context?: string;
	sessionType?: string;
}) => ({
	...(sessionId ? { sessionId: String(sessionId) } : {}),
	...(itemId
		? { itemId, itemVersionId: "1", sessionType: sessionType ?? "FORMATIVE" }
		: {}),
	...(threadId ? { threadId } : {}),
	...(runId ? { runId } : {}),
	...(rating ? { rating } : {}),
	...(featureName ? { featureName } : {}),
	...(responseId ? { responseId } : {}),
	...(context ? { context } : {}),
});
const sendFeedback = async (params: FeedbackParams) => {
	const {
		email,
		feedback,
		type = "Review",
		appLanguage,
		examName,
		view,
		projectId,
		organizationId,
		productId,
		currentScreen,
		featureName,
		itemId,
		sessionId,
		responseId,
		threadId,
		runId,
		itemVersionId,
		sessionType,
		rating,
		context,
	} = params;

	const body = JSON.stringify({
		email,
		appVersion: currentAppVersion,
		projectId: projectId.toString(),
		organizationId: organizationId.toString(),
		feedback,
		platform: Platform.OS,
		currentScreen,
		currentUrl: view,
		featureName,
		itemId,
		sessionId,
		responseId,
		aiThreadId: threadId,
		aiRunId: runId,
		...(runId ? { aiTraceId: RUN_LANGSMITH_URL + runId } : {}),
		appContext: context ?? examName,
		bundleId: productId.toString(),
		source: type,
		itemVersionId,
		sessionType,
		rating,
		appLanguage,
	});

	await fetch(ENDPOINT_FEEDBACK, {
		method: "POST",
		body,
	});
};
export { getShouldShowReviewPrompt, sendFeedback };
